import { useState, useEffect, useCallback } from "react";
import { trackError, ErrorSeverity } from "../utils/error-tracking";
import { transportFailureContext } from "../utils/fetch-transport-failure";
import { usePagedList } from "./search-listing/usePagedList";
import { PAGE_SIZE } from "../data/js/pagination";
import { rankListingsByInterest } from "../utils/session-interest";
import { parseJsonResponse } from "../utils/read-json-response";
import type { ListingSort } from "../utils/listing-sort";
import { appendPageLoadAt, appendShuffleSeed } from '../utils/seeded-shuffle';
import { currentPageKey, recallPageState, rememberPageState } from "../utils/browse-list-cache";
import { isBackNavigation } from "../utils/navigation-direction";
import { asError } from '../hooks/as-error';

/** Response shape shared by the listing endpoints these hooks page through. */
type ListingsPage = {
  success?: boolean;
  listings?: Record<string, unknown>[];
  hasMore?: boolean;
};

/**
 * Hook for fetching budget finds listings (under $100).
 */
export function useBudgetFindsListings() {
  // Back puts back the row this page showed (utils/browse-list-cache.ts).
  const [keepKey] = useState(() => `budget-finds:${currentPageKey()}`);
  const [kept] = useState(() =>
    isBackNavigation() ? recallPageState<{ listings: Record<string, unknown>[] }>(keepKey) : null
  );
  const [listings, setListings] = useState<Record<string, unknown>[]>(() => kept?.listings ?? []);
  const [isLoading, setIsLoading] = useState(!kept);

  useEffect(() => {
    if (kept) return;
    const fetchListings = async () => {
      // A bare "Failed to fetch" filed 47 rows over 24 days (cluster
      // d20a8197b56b63ff) with no route, no timing and no page state.
      const startedAt = Date.now();
      let stage: 'fetch' | 'parse' = 'fetch';
      let status: number | null = null;
      try {
        const params = new URLSearchParams({
          maxPrice: '100',
          limit: String(PAGE_SIZE.SHOWCASE),
          status: '1,2,3', // Active only
        });
        const response = await fetch(`/api/search/listings?${appendPageLoadAt(appendShuffleSeed(params))}`);
        stage = 'parse';
        status = response.status;
        const data = await parseJsonResponse<ListingsPage>(response, 'GET /api/search/listings');
        if (data.success) {
          // Ordered once on arrival by what this visit has looked at. The
          // server knows the 30-day vector; the browser knows the last minute.
          const ranked = rankListingsByInterest(data.listings || []);
          setListings(ranked);
          // Only an answer is kept: a failed fetch is asked again on Back.
          rememberPageState(keepKey, { listings: ranked });
        }
      } catch (thrown) {
        // fetch and parseJsonResponse only throw Errors; asError keeps the type honest.
        const err = asError(thrown);
        trackError(err, ErrorSeverity.ERROR, {
          action: 'fetchBudgetFindsListings',
          component: 'ForYouPage',
          extra: {
            endpoint: 'GET /api/search/listings',
            stage,
            status,
            elapsedMs: Date.now() - startedAt,
            errorName: err.name,
            ...transportFailureContext(err),
          },
        });
      } finally {
        setIsLoading(false);
      }
    };

    void fetchListings();
  }, [kept, keepKey]);

  return { listings, isLoading };
}

/**
 * A row's first page ordered by what this visit has looked at, once, on
 * arrival: the server ordered it by the 30-day vector, the browser moves what
 * this visit looked at to the front. Later renders keep that order; re-ranking
 * painted cards would shuffle them under the buyer's cursor.
 */
export function useRankedOnce(listings: Record<string, unknown>[]): Record<string, unknown>[] {
  const [ranked] = useState(() => rankListingsByInterest(listings));
  return ranked;
}

type GridListing = Record<string, unknown>;

/** Whose list, in which order: a change is a new list. Also the Back cache key. */
const gridListKey = (loggedIn: boolean, userId: number | undefined, sessionId: string, sortBy: ListingSort | undefined) =>
  `foryou:${loggedIn ? `u${userId ?? ''}` : `s${sessionId}`}:${sortBy ?? ''}`;

/**
 * The For You grid's listings, a page at a time, on the shared pager
 * (components/search-listing/usePagedList): pages in order, Back from the list
 * cache, one request per ask.
 */
export function useGridListings(options: {
  searchViewMode: string;
  loggedIn: boolean;
  userId?: number;
  sessionId: string;
  /** A picked sort (?sortBy=). Absent keeps the personalized order. */
  sortBy?: ListingSort;
}) {
  const { searchViewMode, loggedIn, userId, sessionId, sortBy } = options;

  const fetchPage = useCallback(async (page: number) => {
    const params = new URLSearchParams({
      type: loggedIn ? 'user' : 'session',
      ...(loggedIn && userId ? { userId: String(userId) } : {}),
      ...(!loggedIn && sessionId ? { sessionId } : {}),
      page: String(page),
      limit: String(PAGE_SIZE.LISTINGS),
      ...(sortBy ? { sortBy } : {}),
    });
    const response = await fetch(`/api/recommendations/listings?${appendPageLoadAt(appendShuffleSeed(params))}`);
    const data = await parseJsonResponse<ListingsPage>(response, 'GET /api/recommendations/listings');
    if (!data.success) throw new Error('recommendations/listings returned success: false');
    return { items: (data.listings || []) as GridListing[], hasMore: data.hasMore || false };
  }, [loggedIn, userId, sessionId, sortBy]);

  const pager = usePagedList<GridListing>({
    listKey: gridListKey(loggedIn, userId, sessionId, sortBy),
    fetchPage,
    enabled: searchViewMode === 'grid' && (loggedIn || !!sessionId),
    onError: (err) => trackError(asError(err), undefined, { action: 'fetchGridListings', component: 'ForYouPage' }),
  });

  return {
    gridListings: pager.list as GridListing[],
    gridPage: pager.page,
    gridHasMore: !pager.done,
    gridIsLoading: pager.loading,
    setGridPage: pager.setPage,
    loadNextGridPage: pager.loadNext,
  };
}
